import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import { ResizeObserver } from "@juggle/resize-observer";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import { debounce } from "ts-debounce";
import React, { useEffect, useState, useMemo } from "react";
import useMeasure from "react-use-measure";
import { Box } from "theme-ui";
import { ArrowRight, ArrowLeft } from "../../index";
const PaginationButtonLeft = {
    justifyContent: "flex-start",
    gradient: "90",
    paginationIncrement: -1
};
const PaginationButtonRight = {
    justifyContent: "flex-end",
    gradient: "270",
    paginationIncrement: 1
};
const PaginationButton = ({ width = "10%", height = "100%", paginate, direction }) => {
    const { justifyContent, gradient, paginationIncrement } = direction === "left" ? PaginationButtonLeft : PaginationButtonRight;
    const Arrow = direction === "left" ? ArrowLeft : ArrowRight;
    return (_jsx("button", { type: "button", "aria-label": `paginate ${direction}`, sx: {
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: justifyContent,
            width: width,
            height: height,
            zIndex: 15,
            px: 8,
            py: 0,
            [direction]: 0,
            top: 0,
            border: "none",
            color: "white",
            backgroundColor: "rgb(0,0,0)",
            background: `linear-gradient(${gradient}deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)`,
            ":hover": {
                background: `linear-gradient(${gradient}deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,.1) 100%)`
            }
        }, onClick: () => paginate(paginationIncrement), children: _jsx(Arrow, { width: "50px", height: "50px" }) }));
};
const swipeConfidenceThreshold = 800;
class FinitePagination {
    constructor(indexedNodes, animationControls, numberOfVisibleColumns, columnWidth) {
        this.page = 0;
        this.indexedNodes = indexedNodes;
        this.animationControls = animationControls;
        this.columnWidth = columnWidth;
        this.maxPage = indexedNodes.length - numberOfVisibleColumns;
    }
    increment(amount) {
        this.page += amount;
        if (!this.canPagePrevious())
            this.page = 0;
        if (!this.canPageNext())
            this.page = this.maxPage;
        return this.page;
    }
    canPageNext() {
        return this.page <= this.maxPage;
    }
    canPagePrevious() {
        return this.page > 0;
    }
    elements() {
        return this.indexedNodes;
    }
    animate() {
        this.animationControls.start({
            x: -1 * this.page * this.columnWidth
        });
    }
}
class InfinitePagination {
    constructor(indexedNodes, animationControls, setCarouselChildren) {
        this.page = -2;
        this.animationControls = animationControls;
        this.indexedNodes = indexedNodes;
        this.setCarouselChildren = setCarouselChildren;
        this.totalNumberOfPages = indexedNodes.length;
    }
    increment(amount) {
        const newPage = this.page + amount;
        const result = newPage % this.totalNumberOfPages;
        this.page = result < 0 ? this.totalNumberOfPages + result : result;
        return this.page;
    }
    canPageNext() {
        return true;
    }
    canPagePrevious() {
        return true;
    }
    static swap(array, index) {
        const left = array.slice(0, index);
        const right = array.slice(index);
        return [...right, ...left];
    }
    elements() {
        return InfinitePagination.swap(this.indexedNodes, this.page);
    }
    animate() {
        this.animationControls.start({
            display: "inline-block",
            zIndex: 10
        });
        this.setCarouselChildren(this.elements());
    }
}
export const ContinuousCarousel = ({ children, dragEnabled = true, keyEventsEnabled = true, paginationType = "infinite", autoPaginateIntervalInSeconds = 0, animationDurationInSeconds = 2 / 3 }) => {
    const animationDurationMilliseconds = animationDurationInSeconds * 1000;
    const columns = React.Children.count(children);
    const [ref, bounds] = useMeasure({ polyfill: ResizeObserver });
    const [numberOfVisibleColumns, setNumberOfVisibleColumns] = useState(4);
    const [columnWidth, setColumnWidth] = useState((100 / columns) * bounds.width);
    const [buttonWidthPercentage, setButtonWidthPercentage] = useState(10);
    const [carouselWidth, setCarouselWidth] = useState(`calc(23% * ${columns})`);
    const [direction, setDirection] = useState(0);
    const animationControls = useAnimationControls();
    const carouselElements = React.Children.toArray(children).map((child, index) => ({
        index,
        child
    }));
    const [carouselChildren, setCarouselChildren] = useState(carouselElements);
    const [paginationStrategy, setPaginationStrategy] = useState(paginationType == "infinite"
        ? new InfinitePagination(carouselElements, animationControls, setCarouselChildren)
        : new FinitePagination(carouselElements, animationControls, 3, columnWidth));
    const [page, setPage] = useState(paginationStrategy.increment(0));
    useEffect(() => {
        setButtonWidthPercentage(bounds.width > 600 ? 10 : 20);
        setNumberOfVisibleColumns(bounds.width > 900 ? 4 : bounds.width <= 900 && bounds.width > 600 ? 3 : 2);
    }, [bounds.width]);
    useEffect(() => {
        const itemPercentageWidth = columns > numberOfVisibleColumns
            ? (100 - buttonWidthPercentage) / numberOfVisibleColumns
            : 100 / numberOfVisibleColumns;
        setCarouselWidth(`calc(${itemPercentageWidth}% * ${columns + 1})`);
        const itemWidth = (bounds.width - Math.pow(2, numberOfVisibleColumns)) * (itemPercentageWidth / 100);
        setColumnWidth(itemWidth);
    }, [numberOfVisibleColumns]);
    useEffect(() => {
        paginationType == "finite" &&
            setPaginationStrategy(new FinitePagination(carouselElements, animationControls, numberOfVisibleColumns, columnWidth));
    }, [numberOfVisibleColumns, columnWidth]);
    const doPaginate = (increment) => {
        setDirection(increment);
        setPage(paginationStrategy.increment(increment));
    };
    const paginate = useMemo(() => debounce(doPaginate, animationDurationMilliseconds, {
        isImmediate: true
    }), [paginationStrategy]);
    useEffect(() => paginationStrategy.animate(), [page]);
    useEffect(() => {
        if (paginationType !== "infinite" || autoPaginateIntervalInSeconds <= 0)
            return;
        setInterval(() => doPaginate(1), Math.max(animationDurationInSeconds + 0.1, autoPaginateIntervalInSeconds) * 1000);
    }, []);
    useEffect(() => {
        const handleArrows = (event) => {
            var _a;
            // accessibility: stop keyboard interactions with Gallery when user is moving within a radiogroup
            if (((_a = event.target) === null || _a === void 0 ? void 0 : _a.type) === "radio")
                return;
            if (event.code === "ArrowRight") {
                //forward
                paginate(1);
            }
            if (event.code === "ArrowLeft") {
                // back
                paginate(-1);
            }
        };
        if (keyEventsEnabled) {
            window.addEventListener("keydown", handleArrows);
        }
        return () => {
            window.removeEventListener("keydown", handleArrows);
        };
    }, [keyEventsEnabled]);
    return (_jsxs(Box, { ref: ref, sx: {
            display: "block",
            overflow: "hidden",
            position: "relative"
        }, children: [paginationStrategy.canPagePrevious() && (_jsx(PaginationButton, { direction: "left", width: `${buttonWidthPercentage}%`, paginate: paginate })), paginationStrategy.canPageNext() && (_jsx(PaginationButton, { direction: "right", width: `${buttonWidthPercentage}%`, paginate: paginate })), _jsx(motion.div, { sx: {
                    touchAction: "none"
                }, dragElastic: 1, onPanEnd: (_, info) => {
                    if (!dragEnabled)
                        return;
                    const swipe = info.velocity.x;
                    const swipeDirection = info.velocity.x < 0 ? -1 : 1;
                    const swipePower = swipe * swipeDirection;
                    const pagesToMove = Math.min(2, Math.ceil(swipePower / swipeConfidenceThreshold));
                    paginate(-1 * pagesToMove * swipeDirection);
                }, children: _jsx(Box, { as: "ul", sx: {
                        overflow: "none",
                        width: carouselWidth,
                        marginLeft: paginationType == "infinite" ? -2 * columnWidth : 0,
                        pointerEvents: "all"
                    }, children: carouselChildren.map((child, index) => (_jsx(AnimatePresence, { initial: false, custom: { direction }, mode: "popLayout", children: _jsx(motion.li, { custom: index, initial: { zIndex: -5 }, animate: animationControls, transition: {
                                duration: animationDurationInSeconds,
                                ease: "easeInOut"
                            }, exit: { x: -1 * direction * columnWidth }, sx: {
                                display: "inline-block",
                                width: columnWidth,
                                backgroundColor: "white"
                            }, children: child.child }, `${index}-${child.index}`) }, index))) }) })] }));
};
//# sourceMappingURL=ContinuousCarousel.js.map